export const SESSION_EXPIRED_STATUS_CODES = [401, 419] as const;

const SESSION_EXPIRED_STATUS_SET = new Set<number>(
  SESSION_EXPIRED_STATUS_CODES,
);

export const expiresInToTimestamp = (
  expiresIn: unknown,
  now = Date.now(),
): number | null => {
  const seconds = Number(expiresIn);

  if (!Number.isFinite(seconds) || seconds <= 0) {
    return null;
  }

  return now + seconds * 1000;
};

export const isSessionExpiredStatus = (status: unknown): boolean => {
  const numericStatus = Number(status);

  return (
    Number.isFinite(numericStatus) &&
    SESSION_EXPIRED_STATUS_SET.has(numericStatus)
  );
};

export const getResponseStatus = (response: any): number | undefined => {
  const status =
    response?.status ??
    response?.statusCode ??
    response?.response?.status ??
    response?._data?.code ??
    response?._data?.statusCode ??
    response?.data?.code ??
    response?.data?.statusCode ??
    response?.code;
  const numericStatus = Number(status);

  return Number.isFinite(numericStatus) ? numericStatus : undefined;
};

export const isSessionExpiredResponse = (response: any): boolean =>
  isSessionExpiredStatus(getResponseStatus(response));

export const isBackendAuthFailureResponse = (response: any): boolean => {
  if (isSessionExpiredResponse(response)) {
    return true;
  }

  return getResponseMessages(response).some(isBackendAuthFailureMessage);
};

export const getBackendAccessTokenExpiresAt = (
  sessionOrToken: any,
): number | null => {
  const candidates = [
    sessionOrToken?.backendAccessTokenExpiresAt,
    sessionOrToken?.accessTokenExpiresAt,
    sessionOrToken?.user?.backendAccessTokenExpiresAt,
    sessionOrToken?.user?.accessTokenExpiresAt,
    sessionOrToken?.user?.data?.backendAccessTokenExpiresAt,
    sessionOrToken?.user?.data?.accessTokenExpiresAt,
    sessionOrToken?.data?.backendAccessTokenExpiresAt,
    sessionOrToken?.data?.accessTokenExpiresAt,
  ];

  for (const candidate of candidates) {
    const timestamp = normalizeTimestamp(candidate);

    if (timestamp !== null) {
      return timestamp;
    }
  }

  return getJwtExpiresAt(getBackendAccessToken(sessionOrToken));
};

export const isBackendAccessTokenExpired = (
  sessionOrToken: any,
  now = Date.now(),
): boolean => {
  const expiresAt = getBackendAccessTokenExpiresAt(sessionOrToken);

  return expiresAt !== null && expiresAt <= now;
};

export const getBackendAccessToken = (sessionOrToken: any): string | null => {
  const candidates = [
    sessionOrToken?.accessToken,
    sessionOrToken?.access_token,
    sessionOrToken?.token,
    sessionOrToken?.user?.accessToken,
    sessionOrToken?.user?.access_token,
    sessionOrToken?.user?.token,
    sessionOrToken?.user?.data?.accessToken,
    sessionOrToken?.user?.data?.access_token,
    sessionOrToken?.data?.accessToken,
    sessionOrToken?.data?.access_token,
    sessionOrToken?.data?.token,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim() !== '') {
      return candidate;
    }
  }

  return null;
};

export const getJwtExpiresAt = (token: unknown): number | null => {
  if (typeof token !== 'string' || token.trim() === '') {
    return null;
  }

  const [, payload] = token.split('.');

  if (!payload) {
    return null;
  }

  try {
    const decodedPayload = decodeBase64Url(payload);

    if (!decodedPayload) {
      return null;
    }

    const parsedPayload = JSON.parse(decodedPayload);

    return normalizeTimestamp(parsedPayload?.exp);
  } catch {
    return null;
  }
};

const normalizeTimestamp = (value: unknown): number | null => {
  if (value instanceof Date) {
    const timestamp = value.getTime();

    return Number.isFinite(timestamp) ? timestamp : null;
  }

  if (typeof value === 'number') {
    return normalizeNumericTimestamp(value);
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const numericValue = Number(value);

    if (Number.isFinite(numericValue)) {
      return normalizeNumericTimestamp(numericValue);
    }

    const parsed = Date.parse(value);

    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const normalizeNumericTimestamp = (value: number): number | null => {
  if (!Number.isFinite(value) || value <= 0) {
    return null;
  }

  return value < 10_000_000_000 ? value * 1000 : value;
};

const decodeBase64Url = (value: string): string | null => {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(
    normalized.length + ((4 - (normalized.length % 4)) % 4),
    '=',
  );

  if (typeof globalThis.atob === 'function') {
    return globalThis.atob(padded);
  }

  const bufferConstructor = (globalThis as any).Buffer;

  if (bufferConstructor) {
    return bufferConstructor.from(padded, 'base64').toString('utf-8');
  }

  return null;
};

const getResponseMessages = (response: any): string[] =>
  [
    response?.message,
    response?.statusMessage,
    response?._data?.message,
    response?._data?.error,
    response?.data?.message,
    response?.data?.error,
    response?.response?.data?.message,
    response?.response?.data?.error,
  ].filter((message): message is string => typeof message === 'string');

const isBackendAuthFailureMessage = (message: string): boolean =>
  /token (?:is )?expired/i.test(message) ||
  /token violates .*mandatory constraints/i.test(message) ||
  /resource owner or authorization server denied/i.test(message) ||
  /unauthenticated/i.test(message) ||
  /session expired/i.test(message);
